import { components } from "@/api-schema";
import { SubjectRoles } from "@/api-schema-enums";
import { hasDevStageDeveloperAccess } from "@/common/helpers/has-dev-stage-developer-access";
import type { FeatureFlag } from "@/config/feature-flags";
import {
  AppRoleRequirement,
  NavigationItem,
  NavigationPermission,
  PermissionGroup,
  PermissionOperator,
  PermissionRequirement,
} from "@/config/navigation/types";
import { AnyAbility } from "@casl/ability";

/**
 * Checks if the user has the required permissions.
 * Supports nested permission groups with AND/OR operators.
 *
 * @param ability - The CASL ability instance.
 * @param permission - The permission requirement or group to check.
 * @param userRoles - The list of roles the user has (includes SubjectRoles.Int or SubjectRoles.Ext).
 * @returns True if the user has permission, false otherwise.
 */
export function checkPermission(
  ability: AnyAbility,
  permission: NavigationPermission,
  userRoles?: components["schemas"]["SubjectRoles"][],
): boolean {
  if ("operator" in permission) {
    const group = permission as PermissionGroup;
    if (group.operator === PermissionOperator.AND) {
      return group.permissions.every((p) => checkPermission(ability, p, userRoles));
    }
    return group.permissions.some((p) => checkPermission(ability, p, userRoles));
  }

  if ("appRole" in permission) {
    const req = permission as AppRoleRequirement;
    return hasDevStageDeveloperAccess(userRoles) || (userRoles?.includes(req.appRole) ?? false);
  }

  const req = permission as PermissionRequirement;
  return ability.can(req.action, req.subject);
}

/**
 * Filters navigation items based on permissions and feature flags.
 * Recursively filters children while preserving matching parents, even when children become empty.
 *
 * @param items - The list of navigation items to filter.
 * @param ability - The CASL ability instance.
 * @param featureFlags - A record of feature flags and their values.
 * @param userRoles - The list of roles the user has (includes SubjectRoles.Int or SubjectRoles.Ext).
 * @returns The filtered list of navigation items.
 */
export function filterSidebarItems(items: NavigationItem[]): NavigationItem[] {
  return items.reduce((acc, item) => {
    if (item.hideFromSidebar) {
      if (item.children?.length) {
        acc.push(...filterSidebarItems(item.children));
      }
      return acc;
    }

    if (item.children) {
      const filteredChildren = filterSidebarItems(item.children);

      if (filteredChildren.length === 0) {
        if (item.navigable === false || !item.routePath) {
          return acc;
        }

        acc.push({ ...item, children: [] });
        return acc;
      }

      acc.push({ ...item, children: filteredChildren });
      return acc;
    }

    acc.push(item);
    return acc;
  }, [] as NavigationItem[]);
}

export function filterRoutes(
  items: NavigationItem[],
  ability: AnyAbility | undefined,
  featureFlags: Partial<Record<FeatureFlag, boolean>>,
  userRoles?: SubjectRoles[],
): NavigationItem[] {
  return items.reduce((acc, item) => {
    if (isFeatureFlagDisabled(item, featureFlags)) {
      if (
        !item.featureFlagFallbackPermission ||
        !ability ||
        !checkPermission(ability, item.featureFlagFallbackPermission, userRoles)
      ) {
        return acc;
      }
    }

    if (!hasNavigationAccess(item, ability, userRoles)) {
      return acc;
    }

    const permittedItem = item.commands
      ? { ...item, commands: item.commands.filter((command) => hasNavigationAccess(command, ability, userRoles)) }
      : item;

    if (permittedItem.children) {
      const filteredChildren = filterRoutes(permittedItem.children, ability, featureFlags, userRoles);

      acc.push({ ...permittedItem, children: filteredChildren });
      return acc;
    }

    acc.push(permittedItem);
    return acc;
  }, [] as NavigationItem[]);
}

function isFeatureFlagDisabled(item: NavigationItem, featureFlags: Partial<Record<FeatureFlag, boolean>>): boolean {
  return item.featureFlag !== undefined && featureFlags[item.featureFlag] === false;
}

function hasNavigationAccess(
  item: { permission?: NavigationPermission },
  ability: AnyAbility | undefined,
  userRoles?: SubjectRoles[],
): boolean {
  if (!item.permission || !ability) return true;
  return checkPermission(ability, item.permission, userRoles);
}

export function getFirstNavigationUrl(item?: NavigationItem): string | undefined {
  if (!item) return undefined;
  if (item.navigable !== false && item.routePath && item.component) return item.routePath;
  if (!item.children) return undefined;

  for (const child of item.children) {
    const childUrl = getFirstNavigationUrl(child);
    if (childUrl) return childUrl;
  }

  return undefined;
}

export function getNavigationLinkTarget(item?: NavigationItem): string | undefined {
  if (item?.navigable === false) return undefined;
  const path = getFirstNavigationUrl(item);
  if (!path) return undefined;
  return `~${path}`;
}

export type NavigationLookups = {
  idMap: Map<string, NavigationItem>;
  pathMap: Map<string, NavigationItem>;
  disabledSegments: Set<string>;
  hiddenSegments: Set<string>;
};

export function createNavigationLookups(items: NavigationItem[]): NavigationLookups {
  const lookups: NavigationLookups = {
    idMap: new Map(),
    pathMap: new Map(),
    disabledSegments: new Set(),
    hiddenSegments: new Set(),
  };
  populateNavigationLookups(items, lookups);
  return lookups;
}

function populateNavigationLookups(items: NavigationItem[], lookups: NavigationLookups) {
  for (const item of items) {
    lookups.idMap.set(item.id, item);

    if (item.routePath) {
      lookups.pathMap.set(item.routePath, item);
    }

    if (item.breadcrumbDisableUuidDetails && item.breadcrumbSegment) {
      lookups.disabledSegments.add(item.breadcrumbSegment);
    }

    if (item.hiddenFromBreadcrumb && item.breadcrumbSegment) {
      lookups.hiddenSegments.add(item.breadcrumbSegment);
    }

    if (item.children) {
      populateNavigationLookups(item.children, lookups);
    }
  }
}
